/* -------------------------------------------------------------------------- */
/*  Hero.tsx                                                                  */
/* -------------------------------------------------------------------------- */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Typography, Box } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { FancyTextButton } from "components/Base/Buttons";

const Hero = () => {
  const endings = [
    "an Intentional Strategy.",
    "Better Insight.",
    "Clear Follow-Up.",
    "the Full Picture.",
  ];

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % endings.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        position: "relative",
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        overflow: "hidden",
        py: { xs: 8, md: 14 },
        px: 2,
      }}
    >
      {/* Decorative glow */}
      <Box
        sx={{
          position: "absolute",
          top: "-20%",
          right: "-15%",
          width: 600,
          height: 600,
          background:
            "radial-gradient(circle at center, rgba(77, 222, 128, 0.35), transparent 70%)",
          borderRadius: "50%",
          zIndex: 0,
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              color: "white",
              fontWeight: 800,
              lineHeight: 1.2,
              fontSize: { xs: "2rem", md: "3rem" },
              letterSpacing: -0.6,
            }}
          >
            Lead Evangelism with{" "}
            <Box
              component="span"
              sx={{
                color: "#4ade80",
                display: "inline-block",
                perspective: 1000,
                minWidth: 100,
              }}
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={index}
                  initial={{ rotateX: 90, opacity: 0 }}
                  animate={{ rotateX: 0, opacity: 1 }}
                  exit={{ rotateX: -90, opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  style={{
                    display: "inline-block",
                    transformOrigin: "50% 50%",
                  }}
                >
                  {endings[index]}
                </motion.span>
              </AnimatePresence>
            </Box>
          </Typography>
        </motion.div>

        {/* Subheading */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.7 }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 4,
              color: "rgba(255,255,255,0.82)",
              maxWidth: 1000,
              mx: "auto",
              fontWeight: 400,
              lineHeight: 1.6,
            }}
          >
            Digital tools to create repeatable discipleship pathways that are effective in bringing your community to Christ and can be systematically improved over time.
          </Typography>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
            <FancyTextButton
              fancyVariant="normal"
              size="large"
              href="/register/"
              endIcon={<ArrowForward />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: "1rem",
              }}
            >
              Improve Your Church's Outreach Strategy
            </FancyTextButton>
          </Box>
        </motion.div>
      </Box>

      {/* ================= PRODUCT IMAGES ================= */}
      <Box
        sx={{
          position: "relative",
          mt: { xs: 8, md: 12 },
          height: { xs: 520, sm: 600, md: 460 },
          mx: "auto",
        }}
      >
        {/* Left image */}
        <Box
          component={motion.img}
          src="/Hero/Hero-Left.JPG"
          alt="Community overview"
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          sx={{
            position: "absolute",
            borderRadius: 3,
            boxShadow: "0 30px 80px rgba(0,0,0,0.45)",
            left: { xs: "10%", md: "0%" },
            top: { xs: "0%", md: "0%" },
            width: { xs: "80%", md: "40%" },
            zIndex: { xs: 1, md: 1 },
            opacity: { xs: 1, md: 0.88 },
            border: "1px solid rgba(255,255,255,0.12)",
            backgroundColor: "rgba(255,255,255,0.02)",
          }}
        />

        {/* Center image */}
        <Box
          component={motion.img}
          src="/Hero/Hero-Middle.JPG"
          alt="Campaign planning"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          sx={{
            position: "absolute",
            borderRadius: 3,
            boxShadow: "0 40px 120px rgba(0,0,0,0.6)",
            left: { xs: "10%", md: "25%" },
            top: { xs: "30%", md: "20%" },
            width: { xs: "80%", md: "50%" },
            zIndex: 3,
            border: "1px solid rgba(255,255,255,0.14)",
            backgroundColor: "rgba(255,255,255,0.02)",
          }}
        />

        {/* Right image */}
        <Box
          component={motion.img}
          src="/Hero/Hero-Right.JPG"
          alt="Interactions overview"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          sx={{
            position: "absolute",
            borderRadius: 3,
            boxShadow: "0 30px 80px rgba(0,0,0,0.45)",
            left: { xs: "10%", md: "60%" },
            top: { xs: "60%", md: "0%" },
            width: { xs: "80%", md: "40%" },
            zIndex: { xs: 2, md: 1 },
            opacity: { xs: 1, md: 0.88 },
            border: "1px solid rgba(255,255,255,0.12)",
            backgroundColor: "rgba(255,255,255,0.02)",
          }}
        />
      </Box>
    </Box>
  );
};

export default Hero;